import { Link } from "react-router-dom";
import { m as motion } from "framer-motion";
import { ResponsiveImage } from "../components/ResponsiveImage";
import { usePerformance } from "../lib/performance";
import { ArrowRight, ArrowUpRight, MessageCircle, PackageSearch, Sprout, FileCheck, Ship, Handshake } from "lucide-react";
import { Seo } from "../lib/seo";
import { PRODUCTS, CATEGORIES, STEPS, WA_LINK } from "../lib/site";
import { MaskLine, Eyebrow, Reveal, SectionHead, Marquee, ClipImg, Parallax, CTASection } from "../components/bits";

const HERO_IMG = "https://images.unsplash.com/photo-1595012255680-0a044900356a?q=85&w=1600&auto=format&fit=crop";
const FIELD_IMG = "https://images.unsplash.com/photo-1630873711080-aa3097d1da66?q=85&w=1600&auto=format&fit=crop";

const catImage = (name) => {
  const map = {
    "Millets & Ancient Grains": "photo-1682944625832-fd710e15b190",
    "Pulses & Lentils": "photo-1621956838481-f8f616950454",
    "Seeds & Oilseeds": "photo-1608797178894-bf7c596932da",
    "Superfood Powders": "photo-1565802700474-1c8b57596859",
  };
  return `https://images.unsplash.com/${map[name]}?q=85&w=1200&auto=format&fit=crop`;
};

const Hero = () => {
  const { reducedEffects } = usePerformance();
  return (
  <section className="relative bg-forest-deep text-cream overflow-hidden" data-testid="home-hero">
    <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#FAF7F0 0.7px, transparent 0.7px)", backgroundSize: "18px 18px" }} />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 grid lg:grid-cols-12 gap-14 items-center min-h-[calc(100vh-72px)]">
      <div className="lg:col-span-7">
        <motion.div initial={reducedEffects ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.05 }}>
          <Eyebrow light className="mb-6">Export House · Hyderabad, Telangana, India · 17.3850° N, 78.4867° E</Eyebrow>
        </motion.div>
        <h1 className="font-serif font-bold uppercase tracking-tight leading-[0.95] text-[12.5vw] sm:text-7xl lg:text-[5.6rem]">
          <MaskLine delay={0.25}>Healthy foods,</MaskLine>
          <MaskLine delay={0.38}>exported with</MaskLine>
          <MaskLine delay={0.51}>
            <span className="text-leaf">conviction</span><span className="text-tang">.</span>
          </MaskLine>
        </h1>
        <motion.p
          initial={reducedEffects ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="mt-7 max-w-xl text-base sm:text-lg text-cream/70 leading-relaxed"
        >
          WHY NOT HEALTHY FOODS is a B2B export desk from Hyderabad supplying millets, ancient grains,
          pulses and superfoods — in bulk and private label, with the paperwork done right.
          <span className="font-serif italic text-gold"> Why not from India?</span>
        </motion.p>
        <motion.div
          initial={reducedEffects ? false : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/request-quote"
            data-testid="hero-quote-btn"
            className="inline-flex items-center justify-center gap-2 bg-tang text-white px-8 py-4 font-semibold text-sm tracking-wide hover:bg-[#d96e14] hover:-translate-y-0.5 transition-all duration-300"
          >
            Request a Quote <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={WA_LINK()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 border border-cream/30 px-8 py-4 font-semibold text-sm tracking-wide hover:bg-cream hover:text-forest transition-all duration-300"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp the Export Desk
          </a>
        </motion.div>
        <motion.p
          initial={reducedEffects ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
          className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/40"
        >
          Bulk cargo · Private label · Documentation included · Track your shipment live
        </motion.p>
      </div>
      <div className="lg:col-span-5 relative">
        <motion.div initial={false} animate={{ opacity: 1, scale: 1 }}>
          <Parallax distance={34}>
            <ClipImg src={HERO_IMG} alt="Golden harvest fields in India" className="rotate-2" priority data-testid="home-hero-image" />
          </Parallax>
        </motion.div>
        <div className="float-slow absolute -bottom-6 -left-4 sm:left-8 bg-cream text-forest px-5 py-4 border border-parchment shadow-xl" style={{ "--tilt": "-4deg" }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-tang">Export desk</p>
          <p className="font-serif font-semibold mt-1">Hyderabad → World</p>
        </div>
      </div>
    </div>
  </section>
  );
};

const CategoryBento = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
      <SectionHead
        eyebrow="What we ship"
        title="Four desks. One export standard."
        sub="An indicative range of what moves out of our Hyderabad desk — final specifications, certificates and availability are always shared on request."
      />
      <Reveal delay={0.15}>
        <Link to="/products" data-testid="home-catalogue-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-forest border-b border-forest/40 pb-1 hover:text-tang hover:border-tang transition-colors">
          Full catalogue <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
    <div className="grid md:grid-cols-12 gap-5">
      {CATEGORIES.map((c, i) => {
        const span = i === 0 ? "md:col-span-5" : i === 1 ? "md:col-span-7" : i === 2 ? "md:col-span-7" : "md:col-span-5";
        return (
          <Reveal key={c.id} delay={0.06 * i} className={`${span} h-full`}>
            <Link
              to="/products"
              data-testid={`home-category-card-${c.id}`}
              className="group relative block h-full min-h-[260px] overflow-hidden border border-parchment bg-forest"
            >
              <ResponsiveImage
                src={catImage(c.name)}
                alt={c.name}
                sizes="(min-width: 1280px) 680px, (min-width: 768px) 55vw, 90vw"
                data-testid={`home-category-image-${c.id}`}
                className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-[1.04] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-ink via-forest-ink/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-7 text-cream">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-leaf mb-2">
                  {String(PRODUCTS.filter((p) => p.cat === c.name).length).padStart(2, "0")} lines · On request
                </p>
                <h3 className="font-serif text-2xl sm:text-3xl font-semibold">{c.name}</h3>
                <p className="mt-2 text-sm text-cream/70 max-w-md">{c.blurb}</p>
              </div>
            </Link>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const WhyNotWay = () => {
  const items = [
    { icon: Sprout, t: "Farm-gate first", d: "Sourcing built on direct relationships with farmer collectives across Telangana and the Deccan — not anonymous spot lots." },
    { icon: FileCheck, t: "Quality before cargo", d: "Specifications agreed in writing before shipment; consignment-wise test parameters and certificates shared on request." },
    { icon: Handshake, t: "Paperwork done right", d: "Certificate of origin, phytosanitary, bill of lading — a complete, clean document set with every consignment." },
    { icon: Ship, t: "Packed to your market", d: "Bulk export bags, container liners or retail-ready private label — packed the way your market demands." },
  ];
  return (
    <section className="bg-sand border-y border-parchment">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHead
            eyebrow="The Why Not way"
            title="A small desk that answers fast, ships clean."
            sub="We keep the operation lean and the communication personal — the way good commodity trade has always worked."
          />
          <Reveal delay={0.2} className="mt-10">
            <Parallax distance={30}>
              <ClipImg src={FIELD_IMG} alt="Golden grain fields" />
            </Parallax>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={0.06 * i}>
              <div className={`flex gap-6 py-8 ${i > 0 ? "border-t border-parchment" : ""}`} data-testid={`why-not-way-${i}`}>
                <span className="font-mono text-tang text-sm pt-1">{String(i + 1).padStart(2, "0")}</span>
                <it.icon className="h-6 w-6 text-forest shrink-0 mt-0.5" strokeWidth={1.6} />
                <div>
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-forest">{it.t}</h3>
                  <p className="mt-2 text-sage leading-relaxed">{it.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const StepsTeaser = () => (
  <section className="bg-forest-deep text-cream">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
      <SectionHead
        light
        eyebrow="From RFQ to destination port"
        title="Six steps. Zero surprises."
        sub="A disciplined export process, refined for first-time importers and trading houses alike."
      />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-cream/10 border border-cream/10">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={0.05 * i} className="bg-forest-deep">
            <Link to="/export-process" data-testid={`home-step-${s.n}`} className="block h-full p-8 hover:bg-forest transition-colors duration-300 group">
              <p className="font-mono text-tang text-sm">{s.n}</p>
              <h3 className="font-serif text-xl font-semibold mt-4 group-hover:text-leaf transition-colors">{s.t}</h3>
              <p className="mt-3 text-sm text-cream/60 leading-relaxed line-clamp-3">{s.d}</p>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2} className="mt-10 text-center">
        <Link to="/export-process" data-testid="home-process-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-leaf border-b border-leaf/40 pb-1 hover:text-tang hover:border-tang transition-colors">
          Walk through the full process <ArrowUpRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const TrackTeaser = () => (
  <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
    <Reveal>
      <Link to="/track" data-testid="home-track-banner" className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border border-parchment bg-white px-8 py-10 hover:border-forest/40 transition-colors duration-300">
        <div className="flex items-start gap-6">
          <span className="p-4 bg-mist text-forest"><PackageSearch className="h-7 w-7" strokeWidth={1.6} /></span>
          <div>
            <Eyebrow className="mb-2">Already an importer with us?</Eyebrow>
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-forest">Track your shipment live.</h3>
            <p className="mt-2 text-sage text-sm">Enter your consignment reference and follow it from our desk to your port.</p>
          </div>
        </div>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-forest border-b border-forest/40 pb-1 group-hover:text-tang group-hover:border-tang transition-colors">
          Track now <ArrowUpRight className="inline h-4 w-4" />
        </span>
      </Link>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="WHY NOT HEALTHY FOODS | Millets, Grains & Superfoods Export House — Hyderabad, India"
        description="WHY NOT HEALTHY FOODS is a B2B export house in Hyderabad, Telangana, India — supplying millets, ancient grains, pulses, seeds & superfoods in bulk and private label, with complete export documentation."
      />
      <Hero />
      <Marquee items={["Millets & Ancient Grains", "Pulses & Lentils", "Seeds & Oilseeds", "Superfood Powders", "Bulk & Private Label", "Hyderabad → World"]} />
      <CategoryBento />
      <WhyNotWay />
      <StepsTeaser />
      <TrackTeaser />
      <CTASection />
    </>
  );
}
